import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { format, parseISO, isToday, isBefore, isAfter, startOfDay } from 'date-fns';
import DescriptionIcon from '@mui/icons-material/Description';
import { formatDateForBackend, formatTime } from '../../../utils/dateUtils';
import ConsultationDialog from './ConsultationDialog';
import PastAppointmentsDialog from './PastAppointmentsDialog';
import DocumentManagementDialog from './DocumentManagementDialog';
import { appointmentOrdersApi } from '../api/appointmentOrdersApi';
import BillingDialog from './BillingDialog';
import InvoicePrintDialog from './InvoicePrintDialog';
import CaseStudyPrintDialog from './CaseStudyPrintDialog';
import ItemForm from '../../settings/items/components/ItemForm';
import { Item } from '../../settings/items/types/item.types';
import { hospitalApi, Hospital } from '../../../api/hospitalApi';
import { AppointmentOrderData } from '../types/appointmentOrders.types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { locationsApi } from '../../settings/locations/api/locationsApi';
import { Location } from '../../settings/locations/types/location.types';
import { OrderItem, orderItemsApi } from '../api/orderItemsApi';
import { inventoryApi } from '../../inventory/api/inventoryApi';
import { itemLocationApi } from '../../settings/items/api/itemLocationApi';
import { ItemLocation } from '../../settings/items/types/item-location.types';
import { ItemInventoryFormData, ItemInventoryDetailFormData } from '../../inventory/types/inventory.types';
import { PatientService } from '../../patients/services/patient.service';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { itemApi } from '../../settings/items/api/itemApi';
import { appointmentOrderApi } from '../../appointment-orders/api/appointmentOrdersApi';
import { AppointmentOrderResponseDto } from '../../appointment-orders/api/types';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  ButtonGroup,
  InputAdornment,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
  EventNote as EventNoteIcon,
  Notes as NotesIcon,
  MedicalServices as MedicalServicesIcon,
  PriorityHigh as PriorityHighIcon,
  LocalHospital as HospitalIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  LocalPharmacy as LocalPharmacyIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { Appointment, AppointmentStatus, LocationDto } from '../types/appointment.types';
import { appointmentApi } from '../api/appointmentApi';
import { toast } from 'react-toastify';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { PaymentSplitData } from '../types/appointmentOrders.types';

interface AppointmentDetailsProps {
  appointment: Appointment;
  onEdit?: () => void;
  onClose?: () => void;
  onStatusUpdate?: (updatedAppointment: Appointment) => void;
}

interface PaymentCollectionsSummaryProps {
  appointmentId: string;
}

interface PreviousPendingPaymentsProps {
  patientId: string;
  currentAppointmentId: string;
}

const PreviousPendingPayments: React.FC<PreviousPendingPaymentsProps> = ({ patientId, currentAppointmentId }) => {
  const [pendingOrders, setPendingOrders] = useState<AppointmentOrderResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPending, setTotalPending] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchPendingPayments = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[Previous Pending] Fetching for patientId:', patientId, 'excluding appointment:', currentAppointmentId);
      
      // Fetch all orders for this patient using the appointment-orders API
      const allOrders = await appointmentOrderApi.getAll({
        patientId: patientId,
        limit: 1000
      });
      
      console.log('[Previous Pending] Total orders fetched:', allOrders.length);
      
      // Filter orders with pending amounts (including current appointment)
      const previousPending = allOrders.filter(order => {
        const orderTotal = order.grandTotal || 0;
        const orderPaid = order.paidAmount || 0;
        const pendingAmount = orderTotal - orderPaid;
        const isPending = pendingAmount > 0;
        const hasValidStatus = ['ORDERED', 'BILLED', 'PARTIALLY_PAID'].includes(order.financialStatus);
        
        return isPending && hasValidStatus;
      });
      
      setPendingOrders(previousPending);
      
      // Calculate total pending
      const total = previousPending.reduce((sum, order) => {
        const orderTotal = order.grandTotal || 0;
        const orderPaid = order.paidAmount || 0;
        return sum + (orderTotal - orderPaid);
      }, 0);
      setTotalPending(total);
      
      console.log('[Previous Pending] Final result:', {
        pendingOrdersCount: previousPending.length,
        totalPending: total,
        willShow: previousPending.length > 0 && total > 0
      });
    } catch (error: any) {
      console.error('Error fetching pending payments:', error);
      setPendingOrders([]);
      setTotalPending(0);
    } finally {
      setLoading(false);
    }
  }, [patientId, currentAppointmentId]);

  useEffect(() => {
    if (patientId) {
      fetchPendingPayments();
    }
  }, [fetchPendingPayments, patientId]);

  console.log('[Previous Pending] Render check:', {
    loading,
    pendingOrdersLength: pendingOrders.length,
    totalPending,
    willRender: !loading && (pendingOrders.length > 0 && totalPending > 0)
  });

  if (loading) {
    return (
      <Box mt={3} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (pendingOrders.length === 0 || totalPending === 0) {
    return null; // Don't show if no pending payments
  }

  return (
    <>
      {/* Compact Warning Button */}
      <Button
        variant="outlined"
        color="warning"
        size="small"
        startIcon={<WarningIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ 
          mt: 1,
          borderWidth: 2,
          fontWeight: 'bold',
          '&:hover': {
            borderWidth: 2,
            bgcolor: 'warning.light'
          }
        }}
      >
        Pending Payments: ₹{totalPending.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
      </Button>

      {/* Pending Payments Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6" fontWeight="bold" color="warning.dark">
              Pending Payments
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={3}>
            This patient has pending payments from previous appointments
          </Typography>
          
          {/* Summary Cards */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                <Typography variant="caption" color="text.secondary">
                  Total Pending
                </Typography>
                <Typography variant="h5" color="error.main" fontWeight="bold">
                  ₹{totalPending.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                <Typography variant="caption" color="text.secondary">
                  Pending Orders
                </Typography>
                <Typography variant="h5" color="warning.dark" fontWeight="bold">
                  {pendingOrders.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="caption" color="text.secondary">
                  Appointments
                </Typography>
                <Typography variant="h5" color="info.dark" fontWeight="bold">
                  {new Set(pendingOrders.map(o => o.appointment?.appointmentNumber)).size}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Detailed Orders List */}
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Pending Orders Details:
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Appointment</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Service/Item</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>Paid</strong></TableCell>
                  <TableCell align="right"><strong>Pending</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingOrders.map((order, index) => {
                  const pendingAmount = (order.grandTotal || 0) - (order.paidAmount || 0);
                  const appointmentDate = order.appointment?.appointmentDate 
                    ? format(new Date(order.appointment.appointmentDate), 'dd MMM yyyy')
                    : '-';
                  return (
                    <TableRow key={index} hover>
                      <TableCell>{order.appointment?.appointmentNumber}</TableCell>
                      <TableCell>{appointmentDate}</TableCell>
                      <TableCell>{order.item?.itemName}</TableCell>
                      <TableCell align="right">₹{(order.grandTotal || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">₹{(order.paidAmount || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="bold">
                          ₹{pendingAmount.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const PaymentCollectionsSummary: React.FC<PaymentCollectionsSummaryProps> = ({ appointmentId }) => {
  const [paymentSummary, setPaymentSummary] = useState<{
    cash: number;
    card: number;
    upi: number;
    netbanking: number;
    waveOff: number;
    other: number;
    total: number;
  }>({
    cash: 0,
    card: 0,
    upi: 0,
    netbanking: 0,
    waveOff: 0,
    other: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const fetchPaymentSummary = useCallback(async () => {
    setLoading(true);
    try {
      // Use the new collections summary endpoint
      const summary = await appointmentOrdersApi.getPaymentCollectionsSummary(appointmentId);
      console.log('[Frontend] Payment Summary Received:', summary);
      setPaymentSummary(summary);
    } catch (error: any) {
      // Skip 404 errors silently - payment endpoint may not be implemented yet
      if (error?.status !== 404) {
        console.error('Error fetching payment summary:', error);
      }
      // Set empty summary on error
      setPaymentSummary({
        cash: 0,
        card: 0,
        upi: 0,
        netbanking: 0,
        waveOff: 0,
        other: 0,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchPaymentSummary();
  }, [fetchPaymentSummary]);

  if (loading) {
    return (
      <Box mt={3} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (paymentSummary.total === 0) {
    return null; // Don't show if no payments
  }

  return (
    <Box mt={3}>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        PAYMENT COLLECTIONS
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {paymentSummary.cash > 0 && (
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  Cash
                </Typography>
                <Typography variant="h6" color="success.main">
                  ₹{paymentSummary.cash.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          )}
          {paymentSummary.card > 0 && (
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  Card
                </Typography>
                <Typography variant="h6" color="info.main">
                  ₹{paymentSummary.card.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          )}
          {paymentSummary.upi > 0 && (
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  UPI
                </Typography>
                <Typography variant="h6" color="primary.main">
                  ₹{paymentSummary.upi.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          )}
          {paymentSummary.netbanking > 0 && (
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  Net Banking
                </Typography>
                <Typography variant="h6" color="warning.main">
                  ₹{paymentSummary.netbanking.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          )}
          {paymentSummary.waveOff > 0 && (
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  Wave Off
                </Typography>
                <Typography variant="h6" color="error.main">
                  ₹{paymentSummary.waveOff.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          )}
          {paymentSummary.other > 0 && (
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  Other
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  ₹{paymentSummary.other.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={4} md={2}>
            <Box textAlign="center" sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Total
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                ₹{paymentSummary.total.toFixed(2)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const AppointmentDetails: React.FC<AppointmentDetailsProps> = ({
  appointment,
  onEdit,
  onClose,
  onStatusUpdate,
}) => {
  // Editable orders state (copied from orders prop)
  const [orders, setOrders] = useState<AppointmentOrderData[]>([]);


  const [editableOrders, setEditableOrders] = useState<AppointmentOrderData[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync editableOrders with orders fetched from backend
  useEffect(() => {
    setEditableOrders(orders.map(order => ({ ...order })));
  }, [orders]);

  // Update a field in local editableOrders
  const handleGridChange = (id: string, field: keyof AppointmentOrderData, value: any) => {
    setEditableOrders(prev =>
      prev.map(item =>
        item.id === id ? { ...item, [field]: field === 'discountValue' ? Number(value) : value } : item
      )
    );
  };

  // Returns true if the row has changes compared to the original order
  const hasOrderChanged = (row: AppointmentOrderData) => {
    const original = orders.find(o => o.id === row.id);
    if (!original) return false;
    return (
      row.price !== original.price ||
      row.discountValue !== original.discountValue ||
      row.quantity !== original.quantity
    );
  };

  // Patch the entire order row if changed
  const handleUpdateOrder = async (row: AppointmentOrderData) => {
    if (!hasOrderChanged(row)) return;
    setIsUpdating(true);
    try {
      await appointmentOrdersApi.updateOrderById(row.id, {
        price: row.price,
        discountValue: row.discountValue,
        quantity: row.quantity
      });
      toast.success('Order updated');
      await refreshOrders();
    } catch (error: any) {
      toast.error('Failed to update order: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Backend update for a single field
  const handleOrderFieldUpdate = async (id: string, field: keyof AppointmentOrderData, value: any) => {
    setIsUpdating(true);
    try {
      await appointmentOrdersApi.updateOrderById(id, { [field]: value });
      toast.success('Order updated');
      await refreshOrders();
    } catch (error: any) {
      toast.error('Failed to update order: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Increment/decrement quantity for order dialog (orderItems state)
  const handleOrderDialogQuantityChange = (id: string, delta: number) => {
    setOrderItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, quantity: Math.max(1, (item.quantity || 1) + delta) } : item
      )
    );
  };

  // Increment/decrement quantity for editableOrders table
  const handleQuantityChange = (id: string, delta: number) => {
    setEditableOrders(prev => {
      const updated = prev.map(item => {
        if (item.id === id) {
          const newQuantity = Math.max(1, (item.quantity || 1) + delta);
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      // Find the updated item and push to backend
      const updatedItem = updated.find(item => item.id === id);
      if (updatedItem) {
        handleOrderFieldUpdate(id, 'quantity', updatedItem.quantity);
      }
      return updated;
    });
  };

  // Delete order row
  const handleDeleteOrder = async (id: string) => {
    setIsUpdating(true);
    try {
      await appointmentOrdersApi.updateOrderById(id, { financialStatus: 'CANCELLED', procedureStatus: 'CANCELLED' });
      setEditableOrders(prev => prev.filter(item => item.id !== id));
      toast.success('Order deleted');
    } catch (error: any) {
      toast.error('Failed to delete order: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  const statusColors: Record<AppointmentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    [AppointmentStatus.SCHEDULED]: 'info',
    [AppointmentStatus.CONFIRMED]: 'primary',
    [AppointmentStatus.ARRIVED]: 'info', // Using 'info' as a fallback for teal
    [AppointmentStatus.IN_PROGRESS]: 'warning',
    [AppointmentStatus.COMPLETED]: 'success',
    [AppointmentStatus.CANCELLED]: 'error',
    [AppointmentStatus.NO_SHOW]: 'warning',
    [AppointmentStatus.RESCHEDULED]: 'secondary',
  } as const;

  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showStatusButtons, setShowStatusButtons] = useState(false);

  // State for order items and payment
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [isDispenseDialogOpen, setIsDispenseDialogOpen] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [paymentSplits, setPaymentSplits] = useState<PaymentSplit[]>([]);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('CASH');
  const [paymentReference, setPaymentReference] = useState('');
  const [splitAmount, setSplitAmount] = useState('');
  
  // Dispense related state
  const [dispensableItems, setDispensableItems] = useState<any[]>([]);
  const [dispensingItems, setDispensingItems] = useState<any[]>([]);
  const [itemStockMap, setItemStockMap] = useState<Record<string, ItemLocation[]>>({});
  const [dispenseRemarks, setDispenseRemarks] = useState('');
  type EditingCell = {
  id: string;
  field: keyof AppointmentOrderData;
  value: string;
  type?: 'PERCENTAGE' | 'FIXED';
};
const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [discountInput, setDiscountInput] = useState('');

  // Billing dialog state
  const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);
  const [billingOrders, setBillingOrders] = useState<AppointmentOrderData[]>([]);
  // Dialog states
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [isCaseStudyPrintDialogOpen, setIsCaseStudyPrintDialogOpen] = useState(false);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  
  // Case study data
  const [consultations, setConsultations] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [isConsultationDialogOpen, setIsConsultationDialogOpen] = useState(false);
  const [isPastAppointmentsDialogOpen, setIsPastAppointmentsDialogOpen] = useState(false);
  const [isNewItemDialogOpen, setIsNewItemDialogOpen] = useState(false);
  const [isChangeDoctorDialogOpen, setIsChangeDoctorDialogOpen] = useState(false);
  
  // Change doctor state
  const [availableDoctors, setAvailableDoctors] = useState<any[]>([]);
  const [selectedNewDoctor, setSelectedNewDoctor] = useState<any>(null);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  
  // Consultation data state
  const [consultationData, setConsultationData] = useState({
    chiefComplaint: appointment.chiefComplaint || '',
    diagnosis: appointment.diagnosis || '',
    historyOfPresentIllness: appointment.historyOfPresentIllness || '',
    notes: appointment.notes || '',
    healthQuestionnaire: {
      conditions: appointment.healthQuestionnaire?.conditions || [],
      additionalNotes: appointment.healthQuestionnaire?.additionalNotes || ''
    }
  });

  // Handle saving consultation data
  const handleSaveConsultation = async (data: {
    chiefComplaint: string;
    diagnosis: string;
    historyOfPresentIllness: string;
    notes: string;
    healthQuestionnaire: {
      conditions: string[];
      additionalNotes: string;
    };
  }) => {
    try {
      // In a real implementation, you would call your API to update the appointment
      console.log('Saving consultation data:', data);
      
      // Update local state
      setConsultationData(data);
      
      // If you have an onStatusUpdate prop, you can call it to update the parent component
      if (onStatusUpdate) {
        onStatusUpdate({
          ...appointment,
          ...data,
          consultationDate: new Date().toISOString(),
        });
      }
      
      // Show success message
      toast.success('Consultation saved successfully');
      
      // Close the dialog
      setIsConsultationDialogOpen(false);
    } catch (error) {
      console.error('Error saving consultation data:', error);
      toast.error('Failed to save consultation');
    }
  };
  // Hospital info for invoice
  const [hospitalInfo, setHospitalInfo] = useState<Hospital | null>(null);

  useEffect(() => {
    hospitalApi.getCompanyProfile()
      .then(data => setHospitalInfo(data))
      .catch(() => setHospitalInfo(null));
  }, []);

  // UI-only discount type state per order row
  const [discountTypes, setDiscountTypes] = useState<Record<string, 'PERCENTAGE' | 'FIXED'>>({});

  // Appointment Orders fetch state
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  // Fetch appointment orders on mount and when appointment.id changes
  useEffect(() => {
    if (!appointment?.id) return;
    setOrdersLoading(true);
    setOrdersError(null);
    appointmentOrdersApi.getOrdersByAppointment(appointment.id)
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setOrdersError('Failed to fetch orders');
        setOrders([]);
      })
      .finally(() => setOrdersLoading(false));
  }, [appointment.id]);

  // Helper to refresh orders after order creation
  const refreshOrders = useCallback(() => {
    if (!appointment?.id) return;
    setOrdersLoading(true);
    setOrdersError(null);
    appointmentOrdersApi.getOrdersByAppointment(appointment.id)
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setOrdersError('Failed to fetch orders');
        setOrders([]);
      })
      .finally(() => setOrdersLoading(false));
  }, [appointment.id]);


  // Master list of service items for BillingDialog
  const [itemsMaster, setItemsMaster] = useState<OrderItem[]>([]);
  const [isLoadingItemsMaster, setIsLoadingItemsMaster] = useState(false);
  const [itemsMasterError, setItemsMasterError] = useState<string | null>(null);

  // Fetch master list when BillingDialog is about to open
  useEffect(() => {
    if (isBillingDialogOpen) {
      setIsLoadingItemsMaster(true);
      setItemsMasterError(null);
      orderItemsApi.getServiceItems()
        .then(items => setItemsMaster(items))
        .catch(err => {
          setItemsMasterError('Failed to load item names');
          setItemsMaster([]);
        })
        .finally(() => setIsLoadingItemsMaster(false));
    }
  }, [isBillingDialogOpen]);

  interface PaymentSplit {
    id: string;
    mode: string;
    amount: number;
    reference?: string;
    date?: string;
  }

  // State for available services and products
  const [availableServices, setAvailableServices] = useState<OrderItem[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState<OrderItem | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch services when search query changes or dialog opens
  useEffect(() => {
    const fetchServices = async () => {
      if (!isOrderDialogOpen) {
        console.log('Order dialog not open, skipping service fetch');
        return;
      }
      
      console.log('Fetching services with query:', searchQuery);
      setIsLoadingServices(true);
      setError(null);
      
      try {
        const services = await orderItemsApi.getServiceItems(searchQuery);
        console.log('Received services:', services);
        
        if (!Array.isArray(services)) {
          console.error('Invalid services data received:', services);
          setError('Invalid data received from server');
          return;
        }
        
        if (services.length === 0) {
          console.warn('No services found for query:', searchQuery);
        }
        
        setAvailableServices(services);
      } catch (err) {
        console.error('Error in fetchServices:', err);
        setError('Failed to load services. Please try again.');
      } finally {
        console.log('Finished loading services');
        setIsLoadingServices(false);
      }
    };
    
    const debounceTimer = setTimeout(fetchServices, 300);
    
    return () => {
      console.log('Cleaning up service fetch');
      clearTimeout(debounceTimer);
    };
  }, [searchQuery, isOrderDialogOpen]);

  // Handle discount update
  const handleUpdateDiscount = (itemId: string) => {
    if (!editingCell || editingCell.field !== 'discountValue') return;

    let discountValue = parseFloat(editingCell.value);
    if (isNaN(discountValue) || discountValue < 0) return;

    const safeType: 'PERCENTAGE' | 'FIXED' = editingCell.type === 'FIXED' ? 'FIXED' : 'PERCENTAGE';

    setOrderItems(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          discount: discountValue,
          discountType: safeType,
          discountValue: safeType === 'PERCENTAGE'
            ? (item.sellingPrice * discountValue) / 100
            : discountValue,
        };
      })
    );
    setEditingCell(null);
    setDiscountInput("");
    console.log('[handleUpdateDiscount] Updated discount for itemId:', itemId, 'New value:', discountValue, 'Type:', safeType);
  };

  // Handle price update
  const handleUpdatePrice = (itemId: string) => {
    if (!editingCell || editingCell.field !== 'price') return;
    
    const newPrice = parseFloat(editingCell.value);
    if (isNaN(newPrice) || newPrice < 0) return;
    
    setOrderItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { 
              ...item, 
              sellingPrice: newPrice,
              // Store original price if not already set
              originalPrice: item.originalPrice || item.sellingPrice
            } 
          : item
      )
    );
    
    setEditingCell(null);
  };

  // Calculate item total with discount
  const getItemTotal = (item: OrderItem) => {
    const itemDiscount = item.discountType === 'PERCENTAGE'
      ? (item.sellingPrice * item.discount) / 100
      : item.discount;
      
    return (item.sellingPrice - itemDiscount) * item.quantity;
  };

  // Handle discount type change
  const handleDiscountTypeChange = (itemId: string, newType: 'PERCENTAGE' | 'FIXED') => {
    setOrderItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          // Reset discount when changing type
          return {
            ...item,
            discountType: newType,
            discount: 0
          };
        }
        return item;
      })
    );
  };
  
  // Handle discount value change
  const handleDiscountChange = (itemId: string, newDiscount: number) => {
    setOrderItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          const discount = Math.max(0, newDiscount || 0);
          const maxDiscount = item.discountType === 'PERCENTAGE' ? 100 : item.sellingPrice;
          const clampedDiscount = Math.min(discount, maxDiscount);
          
          return {
            ...item,
            discount: clampedDiscount
          };
        }
        return item;
      })
    );
  };

  // Handle opening order dialog
  const handleOpenOrderDialog = () => {
    setSearchQuery('');
    setSelectedService(null);
    setOrderItems([]);
    setError(null);
    setIsOrderDialogOpen(true);
  };

  // Handle opening dispense dialog
  const handleOpenDispenseDialog = async () => {
    try {
      setIsUpdating(true);
      
      // Get all orders for this appointment that are not dispensed and are inventory items
      // Make sure to include item details in the API call
      const allOrders = await appointmentOrdersApi.getOrdersByAppointment(appointment.id);
      console.log('All orders for dispense:', allOrders);
      console.log('All order statuses:', allOrders.map((o: any) => o.financialStatus));
      
      // If item data is not loaded, we need to fetch it separately
      const ordersWithItems = await Promise.all(
        allOrders.map(async (order: any) => {
          if (!order.item && order.itemId) {
            try {
              // Fetch item details if not included
              const item = await itemApi.getItemById(order.itemId);
              return { ...order, item };
            } catch (error) {
              console.error(`Failed to fetch item ${order.itemId}:`, error);
              return order;
            }
          }
          return order;
        })
      );
      
      const inventoryOrders = ordersWithItems.filter((order: any) => {
        const isInventoryItem = order.item?.itemType === 'INVENTORY';
        const isNotDispensed = !order.isDispensed;
        const hasValidStatus = ['BILLED', 'PAID', 'PARTIALLY_PAID'].includes(order.financialStatus);
        
        console.log(`Order ${order.id}: itemType=${order.item?.itemType}, isDispensed=${order.isDispensed}, status=${order.financialStatus}, valid=${isInventoryItem && isNotDispensed && hasValidStatus}`);
        
        return isInventoryItem && isNotDispensed && hasValidStatus;
      });

      if (inventoryOrders.length === 0) {
        toast.info('No inventory items available for dispensing');
        return;
      }

      // Get stock information for each item
      const stockPromises = inventoryOrders.map(async (order: any) => {
        try {
          // Get itemId from order.itemId or order.item.id
          const itemId = order.itemId || order.item?.id;
          if (!itemId) {
            console.error('Order missing itemId:', order);
            return {
              order,
              stockLocations: [],
              currentStock: 0
            };
          }

          const stockLocations = await itemLocationApi.getItemLocationsByItemId(itemId, true);
          const appointmentLocationStock = stockLocations.filter(
            (loc: ItemLocation) => loc.locationId === appointment.locationId
          );
          return {
            order,
            stockLocations: appointmentLocationStock,
            currentStock: appointmentLocationStock.reduce((sum, loc) => sum + (loc.balance || 0), 0)
          };
        } catch (error) {
          console.error(`Error fetching stock for item ${order.itemId}:`, error);
          return {
            order,
            stockLocations: [],
            currentStock: 0
          };
        }
      });

      const itemsWithStock = await Promise.all(stockPromises);
      
      // Create stock map for easy lookup
      const stockMap: Record<string, ItemLocation[]> = {};
      itemsWithStock.forEach(({ order, stockLocations }) => {
        const itemId = order.itemId || order.item?.id;
        if (itemId) {
          stockMap[itemId] = stockLocations;
        }
      });

      setItemStockMap(stockMap);
      setDispensableItems(itemsWithStock);
      
      // Initialize dispensing items with default values
      // For partial dispensing, we need to calculate remaining quantity to dispense
      const initialDispensingItems = itemsWithStock.map(({ order, currentStock }) => {
        const orderedQty = order.quantity || 0;
        const alreadyDispensedQty = order.dispensedQuantity || 0; // Get from order's dispensedQuantity field
        const pendingDispenseQty = orderedQty - alreadyDispensedQty;
        
        return {
          orderId: order.id,
          itemId: order.itemId || order.item?.id,
          itemName: order.item?.itemName || 'Unknown Item',
          orderedQty,
          alreadyDispensedQty,
          pendingDispenseQty,
          currentStock,
          dispensingQty: 0, // User input - how much they want to dispense now
          maxDispensable: Math.min(pendingDispenseQty, currentStock)
        };
      });

      setDispensingItems(initialDispensingItems);
      setDispenseRemarks('');
      setIsDispenseDialogOpen(true);
      
    } catch (error: any) {
      console.error('Error loading dispensable items:', error);
      toast.error('Failed to load items for dispensing');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle dispensing quantity change (user input)
  const handleDispensingQtyChange = (itemId: string, newQty: number) => {
    setDispensingItems(prev => 
      prev.map(item => {
        if (item.itemId === itemId) {
          const clampedQty = Math.max(0, Math.min(newQty, item.maxDispensable));
          return { ...item, dispensingQty: clampedQty };
        }
        return item;
      })
    );
  };

  // Handle dispense save
  const handleSaveDispense = async () => {
    try {
      setIsUpdating(true);

      // Filter items that have dispensing quantity > 0
      const itemsToDispense = dispensingItems.filter(item => item.dispensingQty > 0);
      
      if (itemsToDispense.length === 0) {
        toast.error('Please enter dispensed quantities for at least one item');
        return;
      }

      // Create inventory transaction for dispensing
      const transactionData: ItemInventoryFormData = {
        type: 'DISPENSED_TO_PATIENT',
        transactionDate: new Date().toISOString(),
        fromLocationId: appointment.locationId,
        remarks: dispenseRemarks || `Dispensed to patient ${appointment.patient?.fullName || 'Unknown Patient'}`,
        status: 'APPROVED',
        details: []
      };

      // Add details for each item being dispensed
      for (const item of itemsToDispense) {
        const stockLocations = itemStockMap[item.itemId] || [];
        let remainingQty = item.dispensingQty; // Use dispensingQty (user input)

        // Distribute the dispensed quantity across available stock locations (FIFO)
        for (const stockLocation of stockLocations) {
          if (remainingQty <= 0) break;
          
          const availableQty = stockLocation.balance || 0;
          if (availableQty <= 0) continue;

          const qtyToDispense = Math.min(remainingQty, availableQty);
          
          const detail: ItemInventoryDetailFormData = {
            itemId: item.itemId,
            itemLocationId: stockLocation.id, // This is crucial for linking to the specific stock location
            quantity: qtyToDispense,
            mode: 'DEBIT',
            batchNumber: stockLocation.batchNumber || '',
            expiryDate: stockLocation.expiryDate || undefined,
            costPrice: stockLocation.item?.costPrice || 0, // Use the cost price from the item
            appointmentOrderId: item.orderId // Link to the appointment order for tracking
          };

          transactionData.details.push(detail);
          remainingQty -= qtyToDispense;
        }
      }

      // Create the inventory transaction
      console.log('Creating inventory transaction:', transactionData);
      const createdTransaction = await inventoryApi.createInventoryTransaction(transactionData);
      console.log('Created transaction response:', createdTransaction);

      // Update the appointment orders to handle partial dispensing
      const updatePromises = itemsToDispense.map(item => {
        const dispensableItem = dispensableItems.find(d => {
          const orderItemId = d.order.itemId || d.order.item?.id;
          return orderItemId === item.itemId;
        });
        if (dispensableItem) {
          const orderedQty = dispensableItem.order.quantity || 0;
          const previouslyDispensedQty = dispensableItem.order.dispensedQuantity || 0;
          const currentlyDispensingQty = item.dispensingQty;
          const newTotalDispensedQty = previouslyDispensedQty + currentlyDispensingQty;
          
          // Only mark as fully dispensed if all quantity has been dispensed
          const isFullyDispensed = newTotalDispensedQty >= orderedQty;
          
          console.log(`Updating order ${dispensableItem.order.id}: ordered=${orderedQty}, previouslyDispensed=${previouslyDispensedQty}, dispensing=${currentlyDispensingQty}, newTotal=${newTotalDispensedQty}, fullyDispensed=${isFullyDispensed}`);
          
          return appointmentOrdersApi.updateOrderById(dispensableItem.order.id, {
            dispensedQuantity: newTotalDispensedQty, // Update the dispensed quantity
            isDispensed: isFullyDispensed, // Only mark as dispensed if fully dispensed
            procedureStatus: isFullyDispensed ? 'COMPLETED' : 'IN_PROGRESS'
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);

      toast.success('Items dispensed successfully');
      setIsDispenseDialogOpen(false);
      await refreshOrders(); // Refresh the orders table
      
    } catch (error: any) {
      console.error('Error dispensing items:', error);
      toast.error('Failed to dispense items: ' + (error.message || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle new item creation
  const handleNewItemSaved = async (newItem: Item) => {
    setIsNewItemDialogOpen(false);
    // Set search query to the new item's name to help user find and add it
    setSearchQuery(newItem.itemName);
    
    // Force refresh of available services to include the newly created item
    try {
      setIsLoadingServices(true);
      const services = await orderItemsApi.getServiceItems(newItem.itemName);
      setAvailableServices(services);
      
      // Auto-select the newly created item if found
      const createdService = services.find(service => service.id === newItem.id);
      if (createdService) {
        setSelectedService(createdService);
        setSearchQuery(`${createdService.itemName} (₹${createdService.sellingPrice})`);
      }
    } catch (err) {
      console.error('Error refreshing services after item creation:', err);
    } finally {
      setIsLoadingServices(false);
    }
  };

  const handleNewItemDialogClose = () => {
    setIsNewItemDialogOpen(false);
  };

  // Handle adding a new order item
  const handleAddOrderItem = () => {
    if (!selectedService) return;

    // Check if item already exists in the order
    const existingItemIndex = orderItems.findIndex(
      item => item.id === selectedService.id
    );

    if (existingItemIndex >= 0) {
      // If item exists, increase quantity
      setOrderItems(prevItems =>
        prevItems.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      // Add new item with quantity 1
      setOrderItems(prevItems => [
        ...prevItems,
        {
          ...selectedService,
          quantity: 1,
        },
      ]);
    }
    
    setSelectedService(null);
  };

  // Handle removing an order item
  const handleRemoveOrderItem = (itemId: string) => {
    setOrderItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  // Format currency with Indian Rupee symbol
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    .format(amount)
    .replace('₹', '₹'); // Ensure we use the correct Rupee symbol
  };

  // Calculate total amount due
  const calculateTotalDue = useCallback(() => {
    return orderItems.reduce((total, item) => total + getItemTotal(item), 0);
  }, [orderItems]);

  // Calculate amount paid so far
  const calculateAmountPaid = useCallback(() => {
    return paymentSplits.reduce((total, split) => total + split.amount, 0);
  }, [paymentSplits]);

  // Calculate remaining amount due
  // Returns positive when customer owes money, negative when they've overpaid
  const calculateRemainingDue = useCallback(() => {
    const totalDue = calculateTotalDue();
    const amountPaid = calculateAmountPaid();
    return totalDue - amountPaid;
  }, [calculateTotalDue, calculateAmountPaid]);

  // Calculate total discount
  const calculateTotalDiscount = useCallback(() => {
    return orderItems.reduce((total, item) => {
      if (item.discountType === 'PERCENTAGE') {
        return total + (item.sellingPrice * (item.discount / 100) * item.quantity);
      }
      return total + (item.discount * item.quantity);
    }, 0);
  }, [orderItems]);

  // Handle payment split amount change with validation
  const handleSplitAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const amount = parseFloat(value) || 0;
    const remainingDue = calculateRemainingDue();
    
    // Only update if amount is valid and doesn't exceed remaining due
    if (amount >= 0 && amount <= remainingDue) {
      setSplitAmount(value);
    } else if (amount > remainingDue) {
      // If amount exceeds remaining due, set to remaining due
      setSplitAmount(remainingDue.toString());
    } else {
      // For negative values, just set to empty string
      setSplitAmount('');
    }
  };

  // Handle add payment split with validation
  const handleAddPaymentSplit = () => {
    const amount = parseFloat(splitAmount) || 0;
    const remainingDue = calculateRemainingDue();
    
    // Validate amount
    if (amount <= 0) {
      // Show error toast or message
      console.error('Payment amount must be greater than 0');
      return;
    }
    
    if (amount > remainingDue) {
      // Show error toast or message
      console.error(`Payment amount cannot exceed remaining due of ${formatCurrency(remainingDue)}`);
      return;
    }
    
    const newSplit: PaymentSplit = {
      id: uuidv4(),
      mode: selectedPaymentMode,
      amount: amount,
      reference: paymentReference,
      date: new Date().toISOString(),
    };
    
    setPaymentSplits([...paymentSplits, newSplit]);
    setSplitAmount('');
    setPaymentReference('');
  };

  // Remove payment split
  const handleRemovePaymentSplit = (index: number) => {
    setPaymentSplits(paymentSplits.filter((_, i) => i !== index));
  };

  // Handle order save (no payment logic)
  const handleSaveOrder = async () => {
    if (orderItems.length === 0) {
      toast.error('Please add at least one item to the order');
      return;
    }

    setIsUpdating(true);
    const now = new Date().toISOString();
    const currentUserId = 'current-user-id'; // Replace with actual user id

    const orders = orderItems.map(item => ({
      appointmentId: appointment.id,
      patientId: appointment.patientId,
      itemId: item.id,
      orderDate: now,
      originalPrice: item.sellingPrice,
      price: item.sellingPrice,
      quantity: item.quantity,
      total: (item.sellingPrice * item.quantity) - (item.discountType === 'PERCENTAGE' ? (item.sellingPrice * item.quantity * (item.discount || 0) / 100) : ((item.discount || 0) * item.quantity)),
      discountValue: item.discount || 0,
      taxAmount: 0,
      grandTotal: (item.sellingPrice * item.quantity) - (item.discountType === 'PERCENTAGE' ? (item.sellingPrice * item.quantity * (item.discount || 0) / 100) : ((item.discount || 0) * item.quantity)),
      financialStatus: 'ORDERED' as const,
      procedureStatus: 'PENDING' as const,
      isDispensed: item.itemType === 'SERVICE',
      paidAmount: 0,
      createdBy: currentUserId
    }));

    try {
      console.log('Order payload:', orders);
      console.log('API endpoint:', appointmentOrdersApi);
      await Promise.all(orders.map(order => appointmentOrdersApi.createOrder(order)));
      toast.success('Order saved successfully!');
      setIsOrderDialogOpen(false);
      setOrderItems([]);
      await refreshOrders(); 
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save order(s)');
    } finally {
      setIsUpdating(false);
    }
  };

// Pay Later (Billed, no payment)
const handlePayLaterOrder = async () => {
    if (orderItems.length === 0) {
      toast.error('Please add at least one item to the order');
      return;
    }

    setIsUpdating(true);
    const now = new Date().toISOString();
    const currentUserId = 'current-user-id'; // Replace with actual user id

    const orders = orderItems.map(item => ({
      appointmentId: appointment.id,
      patientId: appointment.patientId,
      itemId: item.id,
      orderDate: now,
      originalPrice: item.sellingPrice,
      price: item.sellingPrice,
      quantity: item.quantity,
      total: (item.sellingPrice * item.quantity) - (item.discountType === 'PERCENTAGE' ? (item.sellingPrice * item.quantity * (item.discount || 0) / 100) : ((item.discount || 0) * item.quantity)),
      discountValue: item.discount || 0,
      taxAmount: 0,
      grandTotal: (item.sellingPrice * item.quantity) - (item.discountType === 'PERCENTAGE' ? (item.sellingPrice * item.quantity * (item.discount || 0) / 100) : ((item.discount || 0) * item.quantity)),
      financialStatus: 'BILLED' as const,
      procedureStatus: 'PENDING' as const,
      isDispensed: item.itemType === 'SERVICE',
      paidAmount: 0,
      createdBy: currentUserId
    }));

    try {
      console.log('Order payload:', orders);
      console.log('API endpoint:', appointmentOrdersApi);
      await Promise.all(orders.map(order => appointmentOrdersApi.createOrder(order)));
      toast.success('Order saved successfully!');
      setIsOrderDialogOpen(false);
      setOrderItems([]);
      await refreshOrders(); 
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save order(s)');
    } finally {
      setIsUpdating(false);
    }
  };


  // Handle order submission with payment status
  const handleSubmitOrder = async (paymentStatus: 'SAVED' | 'PENDING_PAYMENT' | 'PAID') => {
    if (orderItems.length === 0) {
      toast.error('Please add at least one item to the order');
      return;
    }

    // For PAID status, ensure payment is complete
    if (paymentStatus === 'PAID' && paymentSplits.length === 0) {
      toast.error('Please add at least one payment method');
      return;
    }

    // For PAID status, ensure payment covers the total
    const total = orderItems.reduce((sum, item) => sum + getItemTotal(item), 0);
    const totalPaid = paymentSplits.reduce((sum, split) => sum + split.amount, 0);
    
    if (paymentStatus === 'PAID' && totalPaid < total) {
      toast.error('Total payment amount is less than the order total');
      return;
    }

    try {
      setIsUpdating(true);
      
      // Prepare order items for API
      const orderItemsPayload = orderItems.map(item => ({
        serviceId: item.id,
        itemName: item.itemName, // Changed from name to itemName to match type
        quantity: item.quantity,
        unitPrice: item.sellingPrice,
        discountType: item.discountType,
        discountValue: item.discount,
        totalPrice: getItemTotal(item)
      }));

      // Calculate totals
      const subtotal = orderItems.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0);
      const totalDiscount = orderItems.reduce((sum, item) => {
        const discount = item.discountType === 'PERCENTAGE' 
          ? (item.sellingPrice * item.quantity * item.discount) / 100 
          : item.discount * item.quantity;
        return sum + discount;
      }, 0);
      const grandTotal = subtotal - totalDiscount;

      // Prepare payment details
      const paymentDetails = {
        paymentStatus: paymentStatus === 'PAID' ? 'PAID' : 'PENDING',
        totalPaid: paymentSplits.reduce((sum, split) => sum + split.amount, 0),
        paymentSplits: paymentSplits.map(split => ({
          paymentMode: split.mode,
          amount: split.amount,
          reference: split.reference || '',
          status: 'COMPLETED'
        }))
      };

      // Prepare order payload
      const orderPayload = {
        appointmentId: appointment.id,
        patientId: appointment.patientId,
        items: orderItemsPayload,
        status: paymentStatus,
        ...paymentDetails,
        subtotal,
        totalDiscount,
        tax: 0, // Add tax calculation if needed
        grandTotal,
        notes: '', // Add notes field if needed
        paymentMethod: paymentSplits.length > 0 ? paymentSplits[0].mode : 'CASH' // Default to first payment method or CASH
      };

      // Log for now - replace with actual API call
      console.log('Submitting order with status:', paymentStatus, orderPayload);
      
      // TODO: Uncomment and implement actual API call
      // const response = await orderApi.createOrder(orderPayload);
      
      // Show success message based on payment status
      let successMessage = 'Order ';
      switch (paymentStatus) {
        case 'SAVED':
          successMessage += 'saved as draft';
          break;
        case 'PENDING_PAYMENT':
          successMessage += 'saved - payment pending';
          break;
        case 'PAID':
          successMessage += 'completed successfully';
          break;
      }
      
      toast.success(successMessage);
      
      // Close dialog and reset form
      setIsOrderDialogOpen(false);
      setOrderItems([]);
      await refreshOrders();
      
    } catch (error: unknown) {
      console.error('Error submitting order:', error);
      let errorMessage = 'Failed to process order';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper functions for date validations
  const isAppointmentToday = (appointmentDate: string | Date) => {
    const date = typeof appointmentDate === 'string' ? new Date(appointmentDate) : appointmentDate;
    return isToday(date);
  };

  const isAppointmentInFuture = (appointmentDate: string | Date) => {
    const date = typeof appointmentDate === 'string' ? new Date(appointmentDate) : appointmentDate;
    return isAfter(startOfDay(date), startOfDay(new Date()));
  };

  // Check if appointment is today or in the past
  const isAppointmentTodayOrPast = (appointmentDate: string | Date) => {
    const date = typeof appointmentDate === 'string' ? new Date(appointmentDate) : appointmentDate;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date <= today;
  };

  // Fetch available doctors
  const fetchDoctors = async () => {
    setIsLoadingDoctors(true);
    try {
      // Import the employee API dynamically to avoid circular imports
      const { employeeApi } = await import('../../settings/employees/api/employeeApi');
      const response = await employeeApi.getAll({
        employeeType: 'DOCTOR',
        status: 'ACTIVE',
        limit: 100 // Get enough doctors
      });
      setAvailableDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      toast.error('Failed to load doctors');
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Handle doctor change
  const handleChangeDoctorSubmit = async () => {
    if (!selectedNewDoctor) {
      toast.error('Please select a doctor');
      return;
    }

    if (selectedNewDoctor.id === appointment.doctorId) {
      toast.error('Selected doctor is the same as current doctor');
      return;
    }

    setIsUpdating(true);
    try {
      // Update appointment with new doctor using the proper API method
      const updatedAppointment = await appointmentApi.updateDoctor(appointment.id, selectedNewDoctor.id);

      toast.success(`Doctor changed to ${selectedNewDoctor.firstName} ${selectedNewDoctor.lastName}`);
      
      // Close dialog and reset
      setIsChangeDoctorDialogOpen(false);
      setSelectedNewDoctor(null);
      
      // Call onStatusUpdate if provided to refresh parent component
      if (onStatusUpdate) {
        onStatusUpdate(updatedAppointment);
      }
      
    } catch (error) {
      console.error('Error changing doctor:', error);
      toast.error('Failed to change doctor');
    } finally {
      setIsUpdating(false);
    }
  };

  // Fetch doctors when change doctor dialog opens
  useEffect(() => {
    if (isChangeDoctorDialogOpen) {
      fetchDoctors();
    }
  }, [isChangeDoctorDialogOpen]);
  
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Form validation schema
  const rescheduleSchema = yup.object().shape({
    appointmentDate: yup.date().required('Date is required').min(new Date(), 'Cannot select past dates'),
    startTime: yup
      .string()
      .required('Start time is required')
      .test('valid-time', 'Invalid time format (HH:MM)', (value) => {
        if (!value) return false;
        return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
      }),
    endTime: yup
      .string()
      .required('End time is required')
      .test('valid-time', 'Invalid time format (HH:MM)', (value) => {
        if (!value) return false;
        return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
      })
      .test('is-after-start', 'End time must be after start time', function(value) {
        const { startTime } = this.parent;
        if (!startTime || !value) return true;
        
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = value.split(':').map(Number);
        
        const startDate = new Date();
        startDate.setHours(startHours, startMinutes, 0, 0);
        
        const endDate = new Date();
        endDate.setHours(endHours, endMinutes, 0, 0);
        
        return endDate > startDate;
      }),
    locationId: yup.string().required('Location is required'),
    priority: yup.string().oneOf(['LOW', 'MEDIUM', 'HIGH', 'URGENT']).required('Priority is required'),
    type: yup.string().oneOf(['CONSULTATION', 'FOLLOW_UP', 'THERAPIES', 'OTC', 'WALK_IN', 'OTHER']).required('Type is required'),
    notes: yup.string(),
    reason: yup.string().trim().min(10, 'Please provide a reason (at least 10 characters)').required('Reason is required')
  });

  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm({
    resolver: yupResolver(rescheduleSchema),
    defaultValues: {
      appointmentDate: appointment.appointmentDate ? parseISO(appointment.appointmentDate) : new Date(),
      startTime: appointment.startTime || '',
      endTime: appointment.endTime || '',
      locationId: appointment.locationId || '',
      priority: appointment.priority || 'MEDIUM',
      type: appointment.type || 'CONSULTATION',
      notes: '',
      reason: ''
    }
  });

  const handleOpenRescheduleDialog = () => {
    // Get current time and calculate end time (30 minutes later)
    const now = new Date();
    const endTime = new Date(now.getTime() + 30 * 60000);
    
    // Set form values with specified defaults
    reset({
      appointmentDate: now, // Current date
      startTime: formatTime(now), // Current time
      endTime: formatTime(endTime), // 30 minutes from now
      locationId: appointment.locationId || '',
      priority: appointment.priority || 'MEDIUM', // Keep existing priority
      type: appointment.type || 'CONSULTATION',
      notes: appointment.notes || '', // Keep existing notes
      reason: ''
    });
    
    setShowRescheduleDialog(true);
  };

  // Watch end time to ensure it's after start time
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');

  // Load locations for the dropdown
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoadingLocations(true);
        const response = await locationsApi.getAll({ 
          page: 1, 
          limit: 100, // Fetch all locations
          isActive: true // Only fetch active locations
        });
        setLocations(response.data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        toast.error('Failed to load locations');
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  const handleReschedule = async (data: any) => {
    try {
      setIsUpdating(true);
      
      // Format the date as YYYY-MM-DD
      const formattedDate = formatDateForBackend(data.appointmentDate);
      
      // Ensure times are in HH:MM format
      const formattedStartTime = formatTime(data.startTime);
      const formattedEndTime = formatTime(data.endTime);
      
      // Prepare the reschedule data
      const rescheduleData = {
        appointmentDate: formattedDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        locationId: data.locationId,
        priority: data.priority,
        type: data.type,
        notes: data.notes || undefined,
        reason: data.reason
      };

      // Call the reschedule endpoint
      const updatedAppointment = await appointmentApi.reschedule(appointment.id, rescheduleData);
      
      toast.success('Appointment rescheduled successfully');
      setShowRescheduleDialog(false);
      
      if (onStatusUpdate) {
        onStatusUpdate(updatedAppointment);
      }
    } catch (error: any) {
      console.error('Failed to reschedule appointment:', error);
      let errorMessage = 'Failed to reschedule appointment. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusLabel = (status: AppointmentStatus): string => {
    switch (status) {
      case AppointmentStatus.SCHEDULED:
        return 'Scheduled';
      case AppointmentStatus.CONFIRMED:
        return 'Confirmed';
      case AppointmentStatus.ARRIVED:
        return 'Arrived';
      case AppointmentStatus.IN_PROGRESS:
        return 'In Progress';
      case AppointmentStatus.COMPLETED:
        return 'Completed';
      case AppointmentStatus.CANCELLED:
        return 'Cancelled';
      case AppointmentStatus.NO_SHOW:
        return 'No Show';
      case AppointmentStatus.RESCHEDULED:
        return 'Rescheduled';
      default:
        return status;
    }
  };

  const handleStatusUpdate = async (newStatus: AppointmentStatus) => {
    if (newStatus === appointment.status) return;
    
    try {
      setIsUpdating(true);
      
      // Update the status via API
      const updatedAppointment = await appointmentApi.updateStatus(appointment.id, newStatus);
      
      // Show success message
      toast.success(`Appointment marked as ${newStatus.toLowerCase().replace('_', ' ')}`);
      
      // Notify parent component about the status update
      if (onStatusUpdate) {
        onStatusUpdate(updatedAppointment);
      }
      
      // Close the status actions dropdown
      setShowStatusButtons(false);
      
      // Return the updated appointment
      return updatedAppointment;
      
    } catch (error) {
      console.error('Failed to update appointment status:', error);
      toast.error(`Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error; // Re-throw to allow parent to handle the error if needed
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle case study print - fetch consultations and questions
  const handleCaseStudyPrint = async () => {
    try {
      let consultationData: any[] = [];
      let questionData: any[] = [];

      // Try to fetch consultation data from API
      try {
        const { consultationApi } = await import('../api/consultationApi');
        const consultationResponse = await consultationApi.getByAppointmentId(appointment.id);
        
        if (consultationResponse) {
          // Format consultation data for case study
          consultationData = [{
            id: consultationResponse.id,
            chiefComplaint: consultationResponse.chiefComplaint || '',
            diagnosis: consultationResponse.diagnosis || '',
            history: consultationResponse.historyOfPresentIllness || '',
            examination: '', // Not available in current consultation type
            treatmentPlan: '', // Not available in current consultation type
            notes: consultationResponse.notes || '',
            additionalConditions: consultationResponse.additionalConditions || '',
            createdAt: consultationResponse.consultationDate || consultationResponse.createdAt
          }];

          // Format questions data
          if (consultationResponse.questions && consultationResponse.questions.length > 0) {
            questionData = consultationResponse.questions.map((q, index) => ({
              id: q.id || index + 1,
              question: q.question,
              answer: q.answer ? 'Yes' : 'No',
              notes: q.notes || '',
              category: 'Health Questionnaire'
            }));
          }
        }
      } catch (apiError) {
        console.log('No consultation data found via API, using appointment fields:', apiError);
        
        // Fallback: Create consultation data from appointment fields
        if (appointment.chiefComplaint || appointment.diagnosis || appointment.historyOfPresentIllness || appointment.notes) {
          consultationData = [{
            id: appointment.id,
            chiefComplaint: appointment.chiefComplaint || '',
            diagnosis: appointment.diagnosis || '',
            history: appointment.historyOfPresentIllness || '',
            examination: '',
            treatmentPlan: '',
            notes: appointment.notes || '',
            createdAt: appointment.consultationDate || appointment.appointmentDate
          }];
        }

        // Fallback: Create questions data from health questionnaire
        if (appointment.healthQuestionnaire) {
          if (appointment.healthQuestionnaire.conditions && appointment.healthQuestionnaire.conditions.length > 0) {
            questionData.push({
              id: 1,
              question: 'Medical Conditions',
              answer: appointment.healthQuestionnaire.conditions.join(', '),
              category: 'Health History'
            });
          }
          if (appointment.healthQuestionnaire.additionalNotes) {
            questionData.push({
              id: 2,
              question: 'Additional Notes',
              answer: appointment.healthQuestionnaire.additionalNotes,
              category: 'Health History'
            });
          }
        }
      }
      
      // Set the data
      setConsultations(consultationData);
      setQuestions(questionData);
      
      console.log('Case study data prepared:', { consultationData, questionData });
      
      // Open the dialog
      setIsCaseStudyPrintDialogOpen(true);
    } catch (error) {
      console.error('Error preparing case study data:', error);
      // Still open the dialog with empty data
      setConsultations([]);
      setQuestions([]);
      setIsCaseStudyPrintDialogOpen(true);
    }
  };

  const priorityIcons: Record<string, React.ReactNode> = {
    URGENT: <PriorityHighIcon color="error" />,
    HIGH: <PriorityHighIcon sx={{ color: 'orange' }} />,
    MEDIUM: <PriorityHighIcon color="warning" />,
    LOW: <PriorityHighIcon sx={{ color: 'success.main' }} />,
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Appointment Details
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
  <Chip
    label={getStatusLabel(appointment.status)}
    color={statusColors[appointment.status] || 'default'}
    size="small"
    sx={{ textTransform: 'capitalize', mr: 1 }}
  />
  {/* Invoice/Order status print (only for relevant financialStatus) */}
  <Box display="flex" alignItems="center" gap={1}>
    {/* Invoice Button (Conditional) */}
    {['ARRIVED', 'COMPLETED', 'CONFIRMED'].includes(appointment.status) && orders && orders.length > 0 && (
      <Box display="flex" alignItems="center" gap={0.5} ml={1}>
        <ReceiptIcon fontSize="small" color="action" />
        <Typography variant="caption" color="textSecondary">
          Invoice
        </Typography>
        <Tooltip title="Print Invoice">
          <IconButton size="small" onClick={() => setIsPrintDialogOpen(true)} aria-label="Print Invoice">
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    )}
    
    {/* Case Study Print Button (Always Visible) */}
    <Box display="flex" alignItems="center" gap={0.5} ml={1}>
      <Typography variant="caption" color="textSecondary">
        Case Study
      </Typography>
      <Tooltip title="Print Case Study">
        <IconButton size="small" onClick={handleCaseStudyPrint} aria-label="Print Case Study">
          <PrintIcon fontSize="small" color="secondary" />
        </IconButton>
      </Tooltip>
    </Box>
    
    {/* Documents Button (Always Visible) */}
    <Box display="flex" alignItems="center" gap={0.5} ml={1}>
      <Tooltip title="Manage Documents">
        <IconButton 
          size="small" 
          onClick={() => setIsDocumentDialogOpen(true)}
          aria-label="Manage Documents"
          color="primary"
        >
          <DescriptionIcon />
        </IconButton>
      </Tooltip>
      
      {/* Change Doctor Button */}
      <Tooltip title="Change Doctor">
        <IconButton 
          size="small" 
          onClick={() => setIsChangeDoctorDialogOpen(true)}
          aria-label="Change Doctor"
          color="info"
        >
          <SwapHorizIcon />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
  {priorityIcons[appointment.priority]}
</Box>
      </Box>
      <Box display="flex" flexWrap="wrap" gap={4} mb={3}>
  {/* Appointment Info */}
  <Box mt={3}>
    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
      APPOINTMENT DETAILS
    </Typography>
    <Typography variant="caption" color="textSecondary" gutterBottom>
      (Rendered at {new Date().toLocaleTimeString()})
    </Typography>
  
    <List dense>
      <ListItem disableGutters>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <PersonIcon color="action" fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={appointment.patientName || appointment.patient?.fullName || 'Unknown Patient'}
        />
      </ListItem>
      <ListItem disableGutters>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <EventIcon color="action" fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={appointment.date ? format(new Date(appointment.date), 'EEEE, MMMM d, yyyy') : 'Date not specified'}
        />
      </ListItem>
      <ListItem disableGutters>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <AccessTimeIcon color="action" fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={`${appointment.time}`}
        />
      </ListItem>
      <ListItem disableGutters>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <MedicalServicesIcon color="action" fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={appointment.type.replace('_', ' ')}
          secondary={appointment.reason || 'No reason provided'}
        />
      </ListItem>
      <ListItem disableGutters>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <LocationIcon color="action" fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={appointment.location?.name || 'No location specified'}
          secondary={`${appointment.location?.addressLine1 || ''} ${appointment.location?.city || ''}`}
        />
      </ListItem>
    </List>
    
    {/* Previous Pending Payments Button */}
    {appointment.patientId && (
      <PreviousPendingPayments 
        patientId={appointment.patientId} 
        currentAppointmentId={appointment.appointmentNumber}
      />
    )}
    
  </Box>

  <Divider orientation="vertical" flexItem />

  {/* Placeholder for future Appointment Actions column */}
  <Box mt={3}>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          APPOINTMENT ACTIONS
        </Typography>
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Button 
              variant="outlined" 
              size="small"
              color="primary"
              onClick={() => setShowStatusButtons(!showStatusButtons)}
              sx={{ minWidth: 120 }}
              startIcon={showStatusButtons ? <CancelIcon /> : <AccessTimeIcon />}
            >
              {showStatusButtons ? 'Hide Actions' : 'Show Actions'}
            </Button>
            
            {/* Start Consultation Button - Only show when status is ARRIVED */}
            {appointment.status === AppointmentStatus.ARRIVED && (
  <>
    <Button
      variant="contained"
      size="small"
      color="primary"
      onClick={() => setIsConsultationDialogOpen(true)}
      startIcon={<EventNoteIcon />}
      sx={{ minWidth: 150 }}
    >
      Start Consultation
    </Button>
    <Button
      variant="outlined"
      size="small"
      color="secondary"
      onClick={() => setIsPastAppointmentsDialogOpen(true)}
      sx={{ minWidth: 180 }}
    >
      View Past Appointments
    </Button>
  </>
)}
          </Box>

          {showStatusButtons && (
            <Box display="flex" flexWrap="wrap" gap={2}>
              {/* Buttons for SCHEDULED and RESCHEDULED status */}
              {(appointment.status === AppointmentStatus.SCHEDULED || 
                appointment.status === AppointmentStatus.RESCHEDULED) && (
                <>
                  <Button 
                    variant="outlined" 
                    size="small"
                    color="info"
                    onClick={() => handleStatusUpdate(AppointmentStatus.ARRIVED)}
                    disabled={isUpdating || !isAppointmentToday(appointment.startTime)}
                    startIcon={<PersonIcon />}
                    title={!isAppointmentToday(appointment.startTime) ? "Can only mark as arrived for today's appointments" : ""}
                  >
                    Mark as Arrived
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    color="error"
                    onClick={() => handleStatusUpdate(AppointmentStatus.CANCELLED)}
                    disabled={isUpdating || isBefore(new Date(appointment.startTime), startOfDay(new Date()))}
                    startIcon={<CancelIcon />}
                    title={isBefore(new Date(appointment.startTime), startOfDay(new Date())) ? "Cannot cancel past appointments" : ""}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    color="warning"
                    onClick={() => handleStatusUpdate(AppointmentStatus.NO_SHOW)}
                    disabled={isUpdating}
                    startIcon={<AccessTimeIcon />}
                  >
                    No Show
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    color="secondary"
                    onClick={handleOpenRescheduleDialog}
                    disabled={isUpdating}
                    startIcon={<ScheduleIcon />}
                    sx={{ minWidth: 140 }}
                  >
                    Reschedule
                  </Button>

                </>
              )}

              {/* Reschedule button for CANCELLED and NO_SHOW status */}
              {(appointment.status === AppointmentStatus.CANCELLED || 
                appointment.status === AppointmentStatus.NO_SHOW) && (
                <Button 
                  variant="outlined" 
                  size="small"
                  color="secondary"
                  onClick={handleOpenRescheduleDialog}
                  disabled={isUpdating}
                  startIcon={<ScheduleIcon />}
                  sx={{ minWidth: 140 }}
                >
                  Reschedule
                </Button>
              )}

              {/* Buttons for IN_PROGRESS status */}
              {appointment.status === AppointmentStatus.IN_PROGRESS && (
                <Button 
                  variant="contained" 
                  size="small"
                  color="success"
                  onClick={() => handleStatusUpdate(AppointmentStatus.COMPLETED)}
                  disabled={isUpdating}
                  startIcon={<CheckCircleIcon />}
                >
                  Complete
                </Button>
              )}

              {/* Order, Bill, and Payment Buttons for ARRIVED, IN_PROGRESS, and COMPLETED statuses */}
              {[AppointmentStatus.ARRIVED, AppointmentStatus.IN_PROGRESS, AppointmentStatus.COMPLETED].includes(appointment.status) && (
                <Box mt={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    PATIENT ACTIONS
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      startIcon={<AddShoppingCartIcon />}
                      onClick={() => setIsOrderDialogOpen(true)}
                      sx={{ minWidth: 140 }}
                    >
                      Order
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<LocalPharmacyIcon />}
                      onClick={handleOpenDispenseDialog}
                      sx={{ minWidth: 140 }}
                    >
                      Dispense
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      startIcon={<ReceiptIcon />}
                      onClick={async () => {
                        setIsUpdating(true);
                        try {
                          const orders = await appointmentOrdersApi.getOrdersByAppointment(appointment.id);
                          const includedStatuses = ['ORDERED', 'BILLED', 'PARTIALLY_PAID'];
const relevantOrders = Array.isArray(orders)
  ? orders.filter((item: any) => {
      const statusMatch = includedStatuses.includes((item.financialStatus || '').toUpperCase());
      // Exclude if paidAmount === grandTotal (avoid duplicate payment)
      const isFullyPaid = typeof item.paidAmount === 'number' && typeof item.grandTotal === 'number' && item.paidAmount >= item.grandTotal;
      return statusMatch && !isFullyPaid;
    })
  : [];
console.log('[BillingDialog Trigger] All fetched order statuses:', Array.isArray(orders) ? orders.map((o: any) => o.financialStatus) : orders);
console.log('[BillingDialog Trigger] Filtered orders:', relevantOrders.map((o: any) => ({id: o.id, status: o.financialStatus, paidAmount: o.paidAmount, grandTotal: o.grandTotal})));
setBillingOrders(relevantOrders);
                          setIsBillingDialogOpen(true);
                        } catch (err: any) {
                          toast.error('Failed to fetch billing items.');
                        } finally {
                          setIsUpdating(false);
                        }
                      }}
                      disabled={isAppointmentInFuture(appointment.startTime)}
                      sx={{ minWidth: 140 }}
                      title={isAppointmentInFuture(appointment.startTime) ? "Cannot generate bill for future appointments" : ""}
                    >
                      Bill & Payments
                    </Button>
                    
                   
                  </Box>
                </Box>
              )}

            </Box>
          )}

          {/* Payment Collections Summary */}
          {[AppointmentStatus.ARRIVED, AppointmentStatus.IN_PROGRESS, AppointmentStatus.COMPLETED].includes(appointment.status) && (
            <PaymentCollectionsSummary appointmentId={appointment.id} />
          )}

{appointment.notes && (
        <Box mt={3}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            NOTES
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="body2">
              {appointment.notes}
            </Typography>
          </Paper>
        </Box>
      )}
        </Box>
      </Box>

      {/* Order details */}
      {/* Patient Info */}
  <Box flex={1} minWidth={500}>
    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
      Order Information {editableOrders.length > 0 && (
                <Box component="span" color="text.secondary" fontSize="1rem">({editableOrders.length})</Box>
              )}
    </Typography>
    <Box display="flex" alignItems="center" mb={2}>
      {/* Orders Table - Consistent with BillingDialog */}
      <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 1, width: '100%' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Item Name</TableCell>
              <TableCell sx={{ width: '13%' }} align="right">Original Price</TableCell>
              <TableCell sx={{ width: '15%' }} align="right">Selling Price</TableCell>
              <TableCell sx={{ width: '10%' }} align="center">Qty</TableCell>
              <TableCell sx={{ width: '15%' }} align="right">Line Total</TableCell>
              <TableCell sx={{ width: '10%' }} align="right">Paid Amount</TableCell>
              <TableCell sx={{ width: '10%' }} align="right">Pending Amount</TableCell>
              <TableCell sx={{ width: '10%' }} align="center">Is Dispensed</TableCell>
              <TableCell sx={{ width: '12%' }} align="center">Procedure Status</TableCell>
              <TableCell sx={{ width: '12%' }} align="center">Financial Status</TableCell>
              <TableCell sx={{ width: '6%' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editableOrders.map((item) => {
              const sellingPrice = item.price || 0;
              const quantity = item.quantity || 1;
              const discount = item.discountValue || 0;
              const lineTotal = (sellingPrice * quantity) - discount;
              const paidAmount = typeof item.paidAmount === 'number' ? item.paidAmount : 0;
              const pendingAmount = typeof item.pendingAmount === 'number' ? item.pendingAmount : 0;
              return (
                <TableRow
                  key={item.id}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    ...(item.financialStatus === 'CANCELLED' ? { opacity: 0.5, pointerEvents: 'none', backgroundColor: '#f5f5f5' } : {})
                  }}
                >
                  <TableCell>{item.item?.itemName || item.itemId}</TableCell>
                  <TableCell align="right">₹{item.originalPrice?.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ cursor: (item.financialStatus === 'PAID' || item.financialStatus === 'PARTIALLY_PAID') ? 'not-allowed' : 'pointer' }}
  onClick={() => {
    if (item.financialStatus === 'PAID' || item.financialStatus === 'PARTIALLY_PAID') return;
    setEditingCell({ id: item.id, field: 'price', value: item.price?.toString() || '' });
  }}
>
                    {editingCell?.id === item.id && editingCell.field === 'price' ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editingCell.value}
                        autoFocus
                        onChange={e => setEditingCell({ ...editingCell, value: e.target.value })}
                        onBlur={() => {
                          handleGridChange(item.id, 'price', Number(editingCell.value));
                          setEditingCell(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            handleGridChange(item.id, 'price', Number(editingCell.value));
                            setEditingCell(null);
                          } else if (e.key === 'Escape') {
                            setEditingCell(null);
                          }
                        }}
                        sx={{ width: 80 }}
                        inputProps={{ min: 0 }}
                      />
                    ) : (
                      <>₹{item.price?.toFixed(2)}</>
                    )}
                  </TableCell>
              
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <IconButton size="small" onClick={() => handleQuantityChange(item.id, -1)} disabled={item.quantity <= 1 || item.financialStatus === 'PAID' || item.financialStatus === 'PARTIALLY_PAID'} aria-label="Decrease quantity">
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Box px={1} minWidth={24} textAlign="center">{item.quantity}</Box>
                      <IconButton size="small" onClick={() => handleQuantityChange(item.id, 1)} disabled={item.financialStatus === 'PAID' || item.financialStatus === 'PARTIALLY_PAID'} aria-label="Increase quantity">
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="right">₹{lineTotal.toFixed(2)}</TableCell>
                  <TableCell align="right">₹{paidAmount.toFixed(2)}</TableCell>
                  <TableCell align="right">₹{pendingAmount.toFixed(2)}</TableCell>
                  <TableCell align="center">{item.isDispensed ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="center">{item.procedureStatus || '-'}</TableCell>
                  <TableCell align="center">{item.financialStatus || '-'}</TableCell>
                  <TableCell align="center">
  <IconButton
  size="small"
  color="primary"
  disabled={item.financialStatus === 'PAID' || item.financialStatus === 'PARTIALLY_PAID'}
  onClick={() => handleUpdateOrder(item)}
  aria-label="Update order"
>
  <CheckIcon fontSize="small" />
</IconButton>
  <IconButton
    size="small"
    color="error"
    disabled={!(
      (item.financialStatus === 'ORDERED' || item.financialStatus === 'BILLED') &&
      !item.isDispensed &&
      (item.procedureStatus === 'PENDING' || item.procedureStatus === 'CANCELLED')
    )}
    onClick={() => handleDeleteOrder(item.id)}
  >
    <DeleteIcon fontSize="small" />
  </IconButton>
</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  </Box>
  </Box>

      {/* Reschedule Dialog */}
      <Dialog open={showRescheduleDialog} onClose={() => setShowRescheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <form onSubmit={handleSubmit(handleReschedule)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="appointmentDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Appointment Date"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: 'normal',
                          error: !!errors.appointmentDate,
                          helperText: errors.appointmentDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="startTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="time"
                      fullWidth
                      label="Start Time"
                      margin="normal"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      error={!!errors.startTime}
                      helperText={errors.startTime?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="endTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="time"
                      fullWidth
                      label="End Time"
                      margin="normal"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                        min: watchStartTime, // Ensure end time is after start time
                      }}
                      error={!!errors.endTime}
                      helperText={errors.endTime?.message || (watchEndTime && watchEndTime <= watchStartTime ? 'End time must be after start time' : '')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="locationId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Location"
                      margin="normal"
                      disabled={isLoadingLocations}
                      error={!!errors.locationId}
                      helperText={errors.locationId?.message}
                    >
                      {isLoadingLocations ? (
                        <MenuItem disabled>Loading locations...</MenuItem>
                      ) : locations.length === 0 ? (
                        <MenuItem disabled>No locations available</MenuItem>
                      ) : (
                        locations.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Priority"
                      margin="normal"
                      error={!!errors.priority}
                      helperText={errors.priority?.message}
                    >
                      <MenuItem value="LOW">Low</MenuItem>
                      <MenuItem value="MEDIUM">Medium</MenuItem>
                      <MenuItem value="HIGH">High</MenuItem>
                      <MenuItem value="URGENT">Urgent</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Reason for rescheduling"
                      placeholder="Please provide a detailed reason for rescheduling (minimum 10 characters)"
                      multiline
                      rows={3}
                      error={!!errors.reason}
                      helperText={errors.reason?.message}
                      inputProps={{
                        minLength: 10,
                        'aria-label': 'Reason for rescheduling (minimum 10 characters)'
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={3}
                      label="Additional Notes"
                      margin="normal"
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRescheduleDialog(false)}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={isUpdating || (!!watchEndTime && watchEndTime <= watchStartTime)}
            >
              {isUpdating ? 'Saving...' : 'Reschedule'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {(onEdit || onClose) && (
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          {onClose && (
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
          )}
          {onEdit && (
            <Button 
              onClick={onEdit} 
              variant="contained"
              color="primary"
            >
              Edit Appointment
            </Button>
          )}
        </Box>
      )}
      {/* Order Management Dialog */}
      <Dialog 
        open={isOrderDialogOpen} 
        onClose={() => setIsOrderDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Management</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Add Service Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Add Service</Typography>
              <Box display="flex" gap={2} alignItems="center" sx={{ width: '100%' }}>
                <Box ref={searchRef} sx={{ flexGrow: 1, position: 'relative' }}>
                  <TextField
                    label="Search and select service"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);
                      // Clear selection when user types
                      if (selectedService && value !== `${selectedService.itemName} (₹${selectedService.sellingPrice})`) {
                        setSelectedService(null);
                      }
                    }}
                    onFocus={() => {
                      setIsSearchFocused(true);
                      // Show available services when clicking the search field
                      if (searchQuery.length === 0 && availableServices.length === 0) {
                        // Trigger a search with empty query to fetch all services
                        setSearchQuery(' ');
                        setTimeout(() => setSearchQuery(''), 0);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && selectedService) {
                        handleAddOrderItem();
                        setSelectedService(null);
                        setSearchQuery('');
                      }
                    }}
                    InputProps={{
                      endAdornment: isLoadingServices ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : searchQuery ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedService(null);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ) : null,
                    }}
                    error={!!error}
                    helperText={error}
                  />
                  
                  {isSearchFocused && (searchQuery || availableServices.length > 0) && !selectedService && (
                    <Paper 
                      elevation={3} 
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        maxHeight: 250,
                        overflow: 'auto',
                        mt: 0.5,
                        zIndex: 1300,
                        '& .MuiListItem-root': {
                          py: 0.5,
                          minHeight: 'auto',
                          '&:hover': { 
                            backgroundColor: 'action.hover',
                            '& .MuiTypography-root': {
                              color: 'primary.main'
                            }
                          }
                        }
                      }}
                    >
                      <List dense disablePadding>
                        {availableServices.map((service) => (
                          <ListItem 
                            key={service.id}
                            button
                            dense
                            disableGutters
                            onClick={() => {
                              setSelectedService(service);
                              setSearchQuery(`${service.itemName} (₹${service.sellingPrice})`);
                            }}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '&:last-child': {
                                borderBottom: 'none'
                              }
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="body2" noWrap>
                                  {service.itemName} (₹{service.sellingPrice})
                                </Typography>
                              }
                              secondary={
                                <Box component="span" sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.25
                                }}>
                                  {service.itemCode && (
                                    <Typography variant="caption" color="text.secondary">
                                      {service.itemCode}
                                    </Typography>
                                  )}
                                  {service.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {service.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              primaryTypographyProps={{
                                sx: {
                                  fontSize: '0.875rem',
                                  lineHeight: 1.3
                                }
                              }}
                              secondaryTypographyProps={{ 
                                component: 'div',
                                sx: {
                                  '& .MuiTypography-root': {
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2
                                  }
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                        {/* Add New Item option when no services found */}
                        {searchQuery && availableServices.length === 0 && !isLoadingServices && (
                          <ListItem 
                            button
                            dense
                            disableGutters
                            onClick={() => setIsNewItemDialogOpen(true)}
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderTop: '1px dashed',
                              borderColor: 'primary.main',
                              backgroundColor: 'primary.50'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <AddIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" color="primary">
                                  Add New Item: "{searchQuery}"
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  Create a new service/item with this name
                                </Typography>
                              }
                            />
                          </ListItem>
                        )}
                      </List>
                    </Paper>
                  )}
                </Box>
                
                <Box display="flex" gap={1}>
                  <Button 
                    variant="contained" 
                    onClick={() => {
                      if (selectedService) {
                        handleAddOrderItem();
                        setSelectedService(null);
                        setSearchQuery('');
                      }
                    }}
                    disabled={!selectedService || isLoadingServices}
                    startIcon={<AddIcon />}
                    sx={{ minWidth: 100 }}
                  >
                    Add
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={() => setIsNewItemDialogOpen(true)}
                    startIcon={<AddIcon />}
                    sx={{ minWidth: 120 }}
                  >
                    New Item
                  </Button>
                </Box>
              </Box>
            </Box>
            
            {/* Order Items Table */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Order Items</Typography>
              {orderItems.length === 0 ? (
                <Typography color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                  No items added to order
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto', mt: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.5, width: '25%' }}>Item</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, width: '15%' }}>Original</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, width: '15%' }}>Selling</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 1, width: '15%' }}>Qty</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 1, width: '10%' }}>Total</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 1, width: '10%' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderItems.map((item) => (
                        <TableRow key={item.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ py: 0.5 }}>
                            <Box>
                              <div>{item.itemName}</div>
                              {item.itemCode && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.itemCode}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          
                          {/* Original Price */}
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            {formatCurrency(item.originalPrice || item.sellingPrice)}
                          </TableCell>
                          
                          {/* Selling Price */}
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            {editingCell?.id === item.id && editingCell.field === 'price' ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editingCell.value}
                                  onChange={e => setEditingCell({ ...editingCell, value: e.target.value })}
                                  onKeyDown={(e) => e.key === 'Enter' && handleUpdatePrice(item.id)}
                                  autoFocus
                                  sx={{ width: '80px' }}
                                  inputProps={{ 
                                    style: { textAlign: 'right' },
                                    min: 0,
                                    step: '0.01'
                                  }}
                                />
                                <Box display="flex">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleUpdatePrice(item.id)}
                                    color="primary"
                                    sx={{ p: 0.25 }}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => setEditingCell(null)}
                                    color="error"
                                    sx={{ p: 0.25 }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <Box 
                                onClick={() => setEditingCell({ id: item.id, field: 'price', value: item.sellingPrice.toString() })}
                                sx={{
                                  cursor: 'pointer',
                                  p: 0.5,
                                  borderRadius: 1,
                                  '&:hover': { 
                                    bgcolor: 'action.hover',
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {formatCurrency(item.sellingPrice)}
                                {item.originalPrice && item.originalPrice > item.sellingPrice && (
                                  <Typography variant="caption" color="success.main" display="block">
                                    Save {formatCurrency(item.originalPrice - item.sellingPrice)}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                          
                        
                          {/* Quantity */}
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleOrderDialogQuantityChange(item.id, -1)}
                                disabled={item.quantity <= 1}
                                sx={{ p: 0.5 }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography variant="body2">{item.quantity}</Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => handleOrderDialogQuantityChange(item.id, 1)}
                                sx={{ p: 0.5 }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          
                          {/* Total */}
                          <TableCell align="right" sx={{ py: 0.5, px: 1, fontWeight: 'medium' }}>
                            {formatCurrency(getItemTotal(item))}
                          </TableCell>
                          
                          {/* Actions */}
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveOrderItem(item.id)}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>

          {/* Payment Section */}
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4} mt={2}>
              {/* Left Column: Payment Methods */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
               
               
              </Box>

              {/* Right Column: Order Summary */}
              <Box sx={{ 
                minWidth: 250, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1, 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                height: 'fit-content'
              }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(orderItems.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0))}
                  </Typography>
                </Box>
                
                {orderItems.some(item => item.discount > 0) && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Discount:</Typography>
                    <Typography variant="body2" color="error">
                      -{formatCurrency(orderItems.reduce((sum, item) => {
                        const discount = item.discountType === 'PERCENTAGE' 
                          ? (item.sellingPrice * item.quantity * item.discount) / 100 
                          : item.discount * item.quantity;
                        return sum + discount;
                      }, 0))}
                    </Typography>
                  </Box>
                )}
                
                <Divider sx={{ my: 1 }} />
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Total:</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(orderItems.reduce((sum, item) => sum + getItemTotal(item), 0))}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2" color="primary">Paid:</Typography>
                  <Typography variant="body2" color="primary">
                    <strong>{formatCurrency(paymentSplits.reduce((sum, p) => sum + p.amount, 0))}</strong>
                  </Typography>
                </Box>
                
                {/* Subtotal */}
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(orderItems.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0))}
                  </Typography>
                </Box>

                {/* Discount */}
                {calculateTotalDiscount() > 0 && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Discount:</Typography>
                    <Typography variant="body2" color="error.main">
                      -{formatCurrency(calculateTotalDiscount())}
                    </Typography>
                  </Box>
                )}

                {/* Total */}
                <Box display="flex" justifyContent="space-between" borderTop={1} borderColor="divider" pt={1} mt={1}>
                  <Typography variant="subtitle2" fontWeight="bold">Total:</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {formatCurrency(calculateTotalDue())}
                  </Typography>
                </Box>

                {/* Paid */}
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2">Paid:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(calculateAmountPaid())}
                  </Typography>
                </Box>

                {/* Balance/Change Due */}
                <Box display="flex" justifyContent="space-between" mt={1} borderTop={1} borderColor="divider" pt={1}>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    color={calculateRemainingDue() <= 0 ? 'success.main' : 'error.main'}
                  >
                    {calculateRemainingDue() <= 0 ? 'Change Due:' : 'Balance Due:'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    color={calculateRemainingDue() <= 0 ? 'success.main' : 'error.main'}
                  >
                    {calculateRemainingDue() < 0 ? '+' : calculateRemainingDue() > 0 ? '-' : ''}{formatCurrency(Math.abs(calculateRemainingDue()))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1, justifyContent: 'space-between' }}>
          <Button 
            onClick={() => {
              setIsOrderDialogOpen(false);
              setOrderItems([]);
              setPaymentSplits([]);
              setSplitAmount('');
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
  variant="outlined"
  color="primary"
  onClick={handleSaveOrder}
  disabled={orderItems.length === 0 || isUpdating}
  sx={{ minWidth: 120 }}
>
  Save Order
</Button>
            <Button 
              variant="contained"
              color="secondary"
              onClick={handlePayLaterOrder}
              disabled={orderItems.length === 0 || isUpdating}
              sx={{ minWidth: 120 }}
            >
              {isUpdating ? 'Processing...' : 'Pay Later'}
            </Button>
            
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dispense Dialog */}
      <Dialog
        open={isDispenseDialogOpen}
        onClose={() => setIsDispenseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <LocalPharmacyIcon sx={{ mr: 1 }} />
            Dispense Medication
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Transaction Info */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  fullWidth
                  value={format(new Date(), 'yyyy-MM-dd')}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Location"
                  fullWidth
                  value={appointment.location?.name || 'Unknown Location'}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Remarks"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter any remarks about this dispensation"
                  onChange={(e) => setDispenseRemarks(e.target.value)}
                  value={dispenseRemarks}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Items Table */}
          <Typography variant="h6" gutterBottom>Items to Dispense</Typography>
          {dispensableItems.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">No items available for dispensing</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Order Qty</TableCell>
                    <TableCell align="right">Dispensed Qty</TableCell>
                    <TableCell align="right">Pending Dispense</TableCell>
                    <TableCell align="right">Dispensing Qty</TableCell>
                    <TableCell align="right">Current Stock</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dispensingItems.map((item) => {
                    const canDispense = item.currentStock > 0 && item.pendingDispenseQty > 0;
                    const isFullyDispensed = item.alreadyDispensedQty >= item.orderedQty;
                    
                    return (
                      <TableRow key={item.itemId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.itemName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{item.orderedQty}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="primary">
                            {item.alreadyDispensedQty}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={item.pendingDispenseQty === 0 ? 'success.main' : 'warning.main'}
                          >
                            {item.pendingDispenseQty}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleDispensingQtyChange(item.itemId, item.dispensingQty - 1)}
                              disabled={item.dispensingQty <= 0 || !canDispense}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              size="small"
                              type="number"
                              value={item.dispensingQty}
                              onChange={(e) => handleDispensingQtyChange(item.itemId, parseInt(e.target.value) || 0)}
                              inputProps={{ 
                                min: 0, 
                                max: item.maxDispensable,
                                style: { textAlign: 'center', width: '60px' }
                              }}
                              disabled={!canDispense}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleDispensingQtyChange(item.itemId, item.dispensingQty + 1)}
                              disabled={item.dispensingQty >= item.maxDispensable || !canDispense}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={item.currentStock === 0 ? 'error' : 'text.primary'}
                          >
                            {item.currentStock}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={
                              !canDispense && item.currentStock === 0 ? 'Out of Stock' :
                              !canDispense && item.pendingDispenseQty === 0 ? 'Fully Dispensed' :
                              isFullyDispensed ? 'Completed' :
                              item.dispensingQty > 0 ? 'Ready to Dispense' : 'Pending'
                            }
                            color={
                              !canDispense && item.currentStock === 0 ? 'error' :
                              !canDispense && item.pendingDispenseQty === 0 ? 'success' :
                              isFullyDispensed ? 'success' :
                              item.dispensingQty > 0 ? 'warning' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDispenseDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveDispense}
            disabled={isUpdating || dispensingItems.every(item => item.dispensedQty === 0)}
          >
            {isUpdating ? 'Dispensing...' : 'Dispense Items'}
          </Button>
        </DialogActions>
      </Dialog>

    {/* Billing Dialog */}
    <BillingDialog 
      open={isBillingDialogOpen}
      onClose={() => setIsBillingDialogOpen(false)}
      orders={billingOrders}
      loading={isUpdating}
      itemsMaster={itemsMaster}
    />

    {/* Invoice Print Dialog */}
    {hospitalInfo && (
      <InvoicePrintDialog
        open={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
        appointment={appointment}
        orders={orders}
        hospitalInfo={{
          logoUrl: hospitalInfo.logoUrl || '',
          name: hospitalInfo.name,
          address: [hospitalInfo.address, hospitalInfo.city, hospitalInfo.state, hospitalInfo.country].filter(Boolean).join(', '),
          phone: hospitalInfo.phoneNumber || hospitalInfo.mobileNumber || '',
          email: hospitalInfo.email || '',
        }}
      />
    )}

    {/* Case Study Print Dialog */}
    {hospitalInfo && (
      <CaseStudyPrintDialog
        open={isCaseStudyPrintDialogOpen}
        onClose={() => setIsCaseStudyPrintDialogOpen(false)}
        appointment={appointment}
        orders={orders}
        hospitalInfo={{
          logoUrl: hospitalInfo.logoUrl || '',
          name: hospitalInfo.name,
          address: [hospitalInfo.address, hospitalInfo.city, hospitalInfo.state, hospitalInfo.country].filter(Boolean).join(', '),
          phone: hospitalInfo.phoneNumber || hospitalInfo.mobileNumber || '',
          email: hospitalInfo.email || '',
        }}
        consultations={consultations}
        questions={questions}
      />
    )}
    
    {/* Document Management Dialog */}
    <DocumentManagementDialog
      open={isDocumentDialogOpen}
      onClose={() => {
        setIsDocumentDialogOpen(false);
      }}
      appointmentId={appointment.id}
      // In a real implementation, you would fetch the documents for this appointment
      // initialDocuments={appointment.documents || []}
    />

    {/* Consultation Dialog */}
    <ConsultationDialog
      open={isConsultationDialogOpen}
      onClose={() => {
        setIsConsultationDialogOpen(false);
      }}
      appointmentId={appointment.id}
      doctorId={appointment.doctorId}
      patientId={appointment.patientId}
      onSaved={() => {
        // Close consultation dialog and open order dialog
        setIsConsultationDialogOpen(false);
        handleOpenOrderDialog();
      }}
    />

    {/* Past Appointments Dialog */}
    <PastAppointmentsDialog
      open={isPastAppointmentsDialogOpen}
      onClose={() => setIsPastAppointmentsDialogOpen(false)}
      patientId={appointment.patientId}
      currentAppointmentId={appointment.id}
    />

    {/* New Item Creation Dialog */}
    <Dialog
      open={isNewItemDialogOpen}
      onClose={handleNewItemDialogClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Create New Item</DialogTitle>
      <DialogContent>
        <ItemForm
          isDialogMode={true}
          onSaved={handleNewItemSaved}
          onClose={handleNewItemDialogClose}
          initialItemName={searchQuery}
        />
      </DialogContent>
    </Dialog>

    {/* Change Doctor Dialog */}
    <Dialog
      open={isChangeDoctorDialogOpen}
      onClose={() => {
        setIsChangeDoctorDialogOpen(false);
        setSelectedNewDoctor(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Change Doctor</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Current Doctor: {appointment.doctor?.firstName} {appointment.doctor?.lastName}
          </Typography>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select New Doctor</InputLabel>
            <Select
              value={selectedNewDoctor?.id || ''}
              onChange={(e) => {
                const doctor = availableDoctors.find(d => d.id === e.target.value);
                setSelectedNewDoctor(doctor);
              }}
              disabled={isLoadingDoctors}
            >
              {isLoadingDoctors ? (
                <MenuItem disabled>Loading doctors...</MenuItem>
              ) : (
                availableDoctors
                  .filter(doctor => doctor.id !== appointment.doctorId) // Exclude current doctor
                  .map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      {doctor.firstName} {doctor.lastName} - {doctor.designation || 'Doctor'}
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>
          
          {selectedNewDoctor && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2">Selected Doctor:</Typography>
              <Typography variant="body2">
                {selectedNewDoctor.firstName} {selectedNewDoctor.lastName}
              </Typography>
              {selectedNewDoctor.designation && (
                <Typography variant="caption" color="textSecondary">
                  {selectedNewDoctor.designation}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            setIsChangeDoctorDialogOpen(false);
            setSelectedNewDoctor(null);
          }}
          disabled={isUpdating}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleChangeDoctorSubmit}
          disabled={isUpdating || !selectedNewDoctor}
          startIcon={isUpdating ? undefined : <SwapHorizIcon />}
        >
          {isUpdating ? 'Changing...' : 'Change Doctor'}
        </Button>
      </DialogActions>
    </Dialog>
  </Paper>
  );
};

export default AppointmentDetails;
